import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useTheme } from '../../context/ThemeContext';
import { 
  MessageSquare, Send, Bot, User, Loader2, RefreshCw, 
  AlertCircle, CheckCircle 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIPreview({ clinicId }) {
  const { theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `preview_${Date.now()}`);
  const messagesEndRef = useRef(null);

  // Theme classes
  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200';
  const inputBg = theme === 'dark' ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start with welcome message
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: '¡Hola! Soy el asistente virtual. ¿En qué puedo ayudarte? (Este es un modo de prueba)',
      timestamp: new Date().toISOString()
    }]);
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clinic/${clinicId}/ai/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: input,
          session_id: sessionId,
          conversation_history: messages.map(m => ({
            role: m.role,
            content: m.content
          }))
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString(),
          action: data.action_detected
        }]);
      } else if (response.status === 401 || response.status === 403) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Tu sesión ha expirado. Por favor, cierra sesión y vuelve a iniciar sesión.',
          timestamp: new Date().toISOString(),
          error: true
        }]);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: errorData.detail || 'Error al procesar el mensaje. Verifica la configuración del asistente.',
          timestamp: new Date().toISOString(),
          error: true
        }]);
      }
    } catch (error) {
      console.error('Preview error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error de conexión. Intenta de nuevo.',
        timestamp: new Date().toISOString(),
        error: true
      }]);
    }

    setLoading(false);
  };

  const resetConversation = () => {
    setMessages([{
      role: 'assistant',
      content: '¡Hola! Soy el asistente virtual. ¿En qué puedo ayudarte? (Este es un modo de prueba)',
      timestamp: new Date().toISOString()
    }]);
  };

  return (
    <Card className={cardBg}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={`flex items-center gap-2 text-lg ${textPrimary}`}>
              <MessageSquare className="w-5 h-5 text-green-400" />
              Probar Asistente (Sandbox)
            </CardTitle>
            <CardDescription className={textSecondary}>
              Prueba cómo responde tu IA antes de publicar cambios
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={resetConversation}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reiniciar
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Chat Messages */}
        <div 
          className={`h-80 overflow-y-auto rounded-lg p-4 mb-4 ${
            theme === 'dark' ? 'bg-slate-900/50' : 'bg-gray-100'
          }`}
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-3 mb-4 ${msg.role === 'user' ? 'justify-end' : ''}`}
            >
              {msg.role === 'assistant' && (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.error ? 'bg-red-500/20' : 'bg-blue-500/20'
                }`}>
                  <Bot className={`w-4 h-4 ${msg.error ? 'text-red-400' : 'text-blue-400'}`} />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : msg.error
                      ? theme === 'dark' ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'
                      : theme === 'dark' ? 'bg-slate-700 text-white' : 'bg-white text-gray-900'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.action && (
                  <div className="mt-2 pt-2 border-t border-white/20 flex items-center gap-1 text-xs opacity-75">
                    <CheckCircle className="w-3 h-3" />
                    Acción detectada: {msg.action}
                  </div>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-green-400" />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-blue-400" />
              </div>
              <div className={`rounded-lg p-3 ${theme === 'dark' ? 'bg-slate-700' : 'bg-white'}`}>
                <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Escribe un mensaje de prueba..."
            className={inputBg}
            disabled={loading}
          />
          <Button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>

        {/* Tips */}
        <div className={`mt-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-yellow-50 border border-yellow-200'}`}>
          <p className={`text-xs ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'}`}>
            <strong>💡 Prueba estos escenarios:</strong><br />
            • "¿Cuánto cuesta la consulta?"<br />
            • "Quiero agendar una cita"<br />
            • "Me duele mucho" (urgencia)<br />
            • "Quiero hablar con alguien" (handoff)
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
